/**
 * Self-service account client (/me/profile, /me/security).
 * Profile + password + MFA-disable + revoke-all-sessions are real backend
 * (/api/v1/me/*). MFA *enable* uses the existing /api/v1/auth/mfa/enrol(/confirm).
 */
import { apiClient } from "./client";

export interface MyProfile {
  userId: string;
  username: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  mfaEnrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
  avatarUrl: string | null;
  roleCodes: string[];
  /**
   * Effective permission codes (#18746) — used only to decide what the interface offers. Every
   * endpoint enforces its own permission; hiding a menu is a courtesy, not a control.
   */
  permissions: string[];
}

export interface UpdateProfileInput {
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface MfaEnrolResponse {
  secret: string;
  otpAuthUri: string;
}

export const meApi = {
  get(): Promise<MyProfile> {
    return apiClient.get<MyProfile>("/v1/me").then((r) => r.data);
  },
  update(body: UpdateProfileInput): Promise<MyProfile> {
    return apiClient.put<MyProfile>("/v1/me", body).then((r) => r.data);
  },
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return apiClient
      .post("/v1/me/password", { currentPassword, newPassword })
      .then(() => undefined);
  },
  // MFA enable — existing auth endpoints.
  mfaEnrol(): Promise<MfaEnrolResponse> {
    return apiClient
      .post<MfaEnrolResponse>("/v1/auth/mfa/enrol")
      .then((r) => r.data);
  },
  mfaConfirm(code: string): Promise<void> {
    return apiClient
      .post("/v1/auth/mfa/enrol/confirm", { code })
      .then(() => undefined);
  },
  mfaDisable(currentPassword: string): Promise<void> {
    return apiClient
      .post("/v1/me/mfa/disable", { currentPassword })
      .then(() => undefined);
  },
  revokeAllSessions(): Promise<void> {
    return apiClient
      .post("/v1/me/sessions/revoke-all")
      .then(() => undefined);
  },
  uploadAvatar(file: File): Promise<MyProfile> {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post<MyProfile>("/v1/me/avatar", fd)
      .then((r) => r.data);
  },
};

export const meQueryKeys = {
  profile: () => ["me", "profile"] as const,
};
